import { Component } from '@theme/component';

class QRAnytimeDemo extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.teardown();
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
    super.disconnectedCallback();
  }

  setup() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.generation = (this.generation ?? 0) + 1;
    this.control = this.querySelector('[data-anytime-control]');
    this.caption = this.querySelector('[data-anytime-caption]');
    this.tvFrames = [...this.querySelectorAll('[data-anytime-tv]')];
    this.phoneFrames = [...this.querySelectorAll('[data-anytime-phone]')];
    this.labels = this.querySelector('[data-anytime-labels]')?.content;
    if (!this.control || !this.caption || this.tvFrames.length !== 3 || this.phoneFrames.length !== 3 || !this.labels) return;
    this.steps = [...this.querySelectorAll('[data-anytime-step]')];
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.ready = false;
    this.warming = false;
    this.visible = false;
    this.started = false;
    this.done = false;
    this.paused = false;
    this.stage = 2;
    this.remaining = 2800;
    this.renderStage();
    const { signal } = this.abort;
    document.addEventListener('visibilitychange', () => this.sync(), { signal });
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.finish();
      this.sync();
    }, { signal });
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .45);
      this.sync();
    }, { threshold: [0, .45] });
    this.observer.observe(this);
    this.sync();
  }

  teardown() {
    this.stop();
    this.abort?.abort();
    this.abort = null;
    this.observer?.disconnect();
    this.observer = null;
    this.generation = (this.generation ?? 0) + 1;
  }

  warmImages() {
    if (this.warming || this.ready) return;
    this.warming = true;
    const generation = this.generation;
    const images = [...this.querySelectorAll('.qr-home-anytime-scene img')];
    for (const img of images) img.loading = 'eager';
    Promise.all(images.map(img => img.decode())).then(() => {
      if (!this.isConnected || generation !== this.generation) return;
      this.ready = true;
      this.sync();
    }).catch(() => {
      if (!this.isConnected || generation !== this.generation) return;
      this.finish();
      this.control.hidden = true;
    });
  }

  sync() {
    if (!this.control || !this.motion) return;
    if (this.motion.matches) {
      this.finish();
      this.control.hidden = true;
      return;
    }
    if (this.visible && !this.ready) this.warmImages();
    const canRun = this.visible && !document.hidden && this.ready && !this.paused && !this.done;
    if (!canRun) this.stop();
    if (canRun && !this.started) {
      this.started = true;
      this.stage = 0;
      this.remaining = 2800;
      this.renderStage();
    }
    this.control.hidden = !this.ready;
    this.updateControl();
    if (canRun && !this.timer) this.schedule();
  }

  schedule() {
    this.dataset.running = 'true';
    this.deadline = performance.now() + this.remaining;
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.stage === 2) {
        this.finish();
        return;
      }
      this.stage += 1;
      this.remaining = 2800;
      this.renderStage();
      this.sync();
    }, this.remaining);
  }

  stop() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.remaining = Math.max(0, this.deadline - performance.now());
      this.timer = null;
    }
    this.dataset.running = 'false';
  }

  finish() {
    this.stop();
    this.done = true;
    this.stage = 2;
    this.renderStage();
    this.updateControl();
  }

  renderStage() {
    if (!this.tvFrames || !this.phoneFrames || !this.caption || !this.labels) return;
    this.dataset.stage = String(this.stage);
    for (const frame of this.tvFrames) frame.hidden = Number(frame.dataset.anytimeTv) !== this.stage;
    for (const frame of this.phoneFrames) frame.hidden = Number(frame.dataset.anytimePhone) !== this.stage;
    this.caption.textContent = this.labels.querySelector(`[data-phase="${this.stage}"]`)?.textContent ?? '';
    for (const step of this.steps) step.classList.toggle('is-current', Number(step.dataset.anytimeStep) === this.stage);
  }

  updateControl() {
    if (!this.control) return;
    const label = this.done ? this.control.dataset.replay : this.paused ? this.control.dataset.play : this.control.dataset.pause;
    this.control.setAttribute('aria-label', label);
    const text = this.control.querySelector('[data-anytime-control-label]');
    const icon = this.control.querySelector('[data-anytime-control-icon]');
    if (text) text.textContent = label;
    if (icon) icon.textContent = this.done ? '↻' : this.paused ? '▷' : 'Ⅱ';
  }

  toggleMotion() {
    if (this.motion?.matches || !this.ready) return;
    if (this.done) {
      this.done = false;
      this.started = true;
      this.stage = 0;
      this.remaining = 2800;
      this.paused = false;
      this.renderStage();
    } else {
      this.paused = !this.paused;
    }
    this.sync();
  }
}

if (!customElements.get('qr-anytime-demo')) customElements.define('qr-anytime-demo', QRAnytimeDemo);
